import { format, parseISO } from "date-fns";
import type { ReactNode } from "react";

import type { TableColumn } from "@/components/transactions/BaseTransactionTable";
import type { CellDef } from "@/components/transactions/ExpandableRow";
import { cn } from "@/lib/utils";

import type { BlockChange } from "../protocol/protocol";
import { CATEGORY_NAMES, Change, NONE, REVENUE_NAMES } from "./transactionChange";

/**
 * The AI Analysis tab's transaction table at a card's width (32.5rem at most, the screen on a
 * phone): no account column (a row's detail says it), the revenue change under the category so the
 * change is always in view, the date under the description on a phone, and tighter cells. Wrap
 * the table in `CARD_TABLE`.
 */

// Beats the dense table's own `[&_td]:px-2`; the chevron's cell is its 24px button alone.
export const CARD_TABLE =
  "min-w-0 [&_table_td]:px-1.5 [&_table_th]:px-1.5 [&_[data-row-toggle]]:px-0";

const money = (amount: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

/** The columns after the row's own leading ones (a tick): chevron, date, description, amount, change. */
export function cardColumns<T>(phone: boolean, change: TableColumn<T>): TableColumn<T>[] {
  return [
    { label: "", width: 24 },
    ...(phone ? [] : [{ label: "Date", width: "3.25rem" }]),
    { label: "Description", minPx: 56 },
    { label: "Amount", align: "right" as const, width: phone ? "5.25rem" : "5.5rem" },
    { align: "right" as const, width: phone ? "6.5rem" : "7.5rem", ...change },
  ];
}

/** The change column's heading: what moves in it. */
export function changeLabel(category: boolean, revenue: boolean): string {
  return category && revenue ? "Change" : revenue ? "Revenue" : "Category";
}

/** Old over new (struck, then bold), or the value alone, muted, when it stays. */
export function ChangeStack({
  category,
  revenue,
}: {
  category?: BlockChange;
  revenue?: BlockChange;
}) {
  return (
    <span className="flex flex-col items-end gap-0.5">
      {category && <Change change={category} names={CATEGORY_NAMES} />}
      {revenue && <Change change={revenue} names={REVENUE_NAMES} />}
    </span>
  );
}

/** A transaction's cells after the chevron, for `cardColumns`. */
export function cardCells(
  phone: boolean,
  transaction: { date: string; description: string; amount: string; count?: number },
  change: ReactNode
): CellDef[] {
  const day = transaction.date ? format(parseISO(transaction.date), "MMM d") : NONE;
  const amount = Number(transaction.amount || 0);
  return [
    ...(phone ? [] : [{ value: <span className="whitespace-nowrap">{day}</span> } as CellDef]),
    {
      value: (
        <>
          <span className="block truncate">{transaction.description || NONE}</span>
          {phone && <span className="block text-[0.6875rem] text-muted-foreground">{day}</span>}
        </>
      ),
      tooltip: transaction.description || undefined,
    },
    {
      align: "right",
      value: (
        <span className="block whitespace-nowrap">
          <span
            className={cn(
              "font-medium tabular-nums",
              amount >= 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
            )}
          >
            {transaction.amount ? money(amount) : NONE}
          </span>
          {(transaction.count ?? 1) > 1 && (
            <span className="block text-muted-foreground">×{transaction.count}</span>
          )}
        </span>
      ),
    },
    { align: "right", value: change },
  ];
}
